/**
 * Proxy-session-aware auth storage.
 *
 * When the window is a proxy ESS session (opened by an admin via "Access ESS"),
 * all auth keys live in sessionStorage so the admin's localStorage is never
 * touched and the two sessions are fully isolated.
 *
 * Normal sessions use localStorage exactly as before — zero behaviour change.
 */

/** Auth / identity keys that must never leak between admin and proxy ESS windows. */
export const AUTH_SESSION_KEYS = [
  "token",
  "currentUser",
  "companyData",
  "isAuthenticated",
  "userType",
  "userId",
  "employeeId",
  "companyId",
  "superAdminID",
  "rbacSnapshot",
  "companyDataRefreshedAt",
];

export const isEssProxySession = () =>
  typeof window !== "undefined" &&
  sessionStorage.getItem("essProxySession") === "true";

const isProxySession = isEssProxySession;

export const getAuthItem = (key) => {
  if (isProxySession()) {
    const v = sessionStorage.getItem(key);
    if (v !== null) return v;
  }
  return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
};

export const setAuthItem = (key, value) => {
  if (isProxySession()) {
    sessionStorage.setItem(key, value);
  } else {
    localStorage.setItem(key, value);
  }
};

export const removeAuthItem = (key) => {
  if (isProxySession()) {
    sessionStorage.removeItem(key);
  } else {
    localStorage.removeItem(key);
  }
};

export const getAuthJson = (key, fallback = null) => {
  try {
    const raw = getAuthItem(key);
    if (raw == null || raw === "") return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

export const setAuthJson = (key, value) => {
  setAuthItem(key, JSON.stringify(value));
};

/** Clear auth keys for the active session store only (never wipes the other store). */
export const clearAuthSession = () => {
  if (typeof window === "undefined") return;
  if (isProxySession()) {
    AUTH_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } else {
    AUTH_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  }
};
